import type { Dispatch, ReactNode, SetStateAction } from "react";
import { createContext, useContext, useEffect, useMemo, useState } from "react";

import { page as PlusSearchPage } from "scmp-app/pages/plus/search";
import {
  page as SearchPage,
  type RouteFunction as SearchRouteFunction,
} from "scmp-app/pages/search";
import type {
  ArticlesSearchOrderByInput,
  ContentTypeEnum,
} from "scmp-app/queries/__generated__/searchArticleResultPaginationQuery.graphql";

export type ArticlesSearchFilterByDateRange = {
  endDate?: number;
  startDate?: number;
};

type ContextValue = {
  articleTypeIds?: string[];
  contentTypes?: ContentTypeEnum[];
  currentValue: string;
  dateRange?: ArticlesSearchFilterByDateRange;
  orderBy?: ArticlesSearchOrderByInput;
  paywallTypeIds?: string[];
  searchPageType: SearchPageType;
  searchRouteFunction?: SearchRouteFunction;
  sectionIds?: string[];
  setArticleTypeIds?: Dispatch<SetStateAction<string[]>>;
  setContentTypes?: Dispatch<SetStateAction<ContentTypeEnum[]>>;
  setDateRange?: Dispatch<SetStateAction<ArticlesSearchFilterByDateRange | undefined>>;
  setOrderBy?: Dispatch<SetStateAction<ArticlesSearchOrderByInput | undefined>>;
  setPaywallTypeIds?: Dispatch<SetStateAction<string[]>>;
  setSectionIds?: Dispatch<SetStateAction<string[]>>;
};

type SearchPageType = "default" | "plus";

export const SearchContext = createContext<ContextValue>({
  currentValue: "",
  searchPageType: "default",
});

type Props = {
  children: ReactNode;
  currentValue: string;
  searchPageType: SearchPageType;
};

export const SearchContextProvider: React.FunctionComponent<Props> = ({
  children,
  currentValue,
  searchPageType,
}) => {
  const [orderBy, setOrderBy] = useState<ArticlesSearchOrderByInput>();
  const [sectionIds, setSectionIds] = useState<string[]>([]);
  const [paywallTypeIds, setPaywallTypeIds] = useState<string[]>([]);
  const [articleTypeIds, setArticleTypeIds] = useState<string[]>([]);
  const [contentTypes, setContentTypes] = useState<ContentTypeEnum[]>([
    "ARTICLE",
    "VIDEO",
    "GALLERY",
  ]);

  const [dateRange, setDateRange] = useState<ArticlesSearchFilterByDateRange | undefined>();

  const searchRouteFunction = useMemo(
    () => (searchPageType === "default" ? SearchPage.route : PlusSearchPage.route),
    [searchPageType],
  );

  useEffect(() => {
    if (currentValue) return;
    // Clear effect when the search input has been cleared
    setOrderBy(undefined);
    setSectionIds([]);
    setArticleTypeIds([]);
    setContentTypes(["ARTICLE", "VIDEO", "GALLERY"]);
    setPaywallTypeIds([]);
    setDateRange(undefined);
  }, [currentValue, searchPageType]);

  return (
    <SearchContext.Provider
      value={{
        articleTypeIds,
        contentTypes,
        currentValue,
        dateRange,
        orderBy,
        paywallTypeIds,
        searchPageType,
        searchRouteFunction,
        sectionIds,
        setArticleTypeIds,
        setContentTypes,
        setDateRange,
        setOrderBy,
        setPaywallTypeIds,
        setSectionIds,
      }}
    >
      {children}
    </SearchContext.Provider>
  );
};

export const useSearchContext = () => useContext(SearchContext);
